import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EMPTY_USER_TEAM_LIST_CONFIGURATION, EMPTY_USER_TEAM_LIST_CRITERIA } from "../constants/userTeamConstants";
import { UserTeamState } from "../objects/userTeamState";
import { Preferences } from "../../profile/objects/preferences";
import { setUserTeamPreference } from "../utils/setUserTeamPreference";
import { UserTeam } from "../objects/userTeam";
import { UserTeamListCriteria } from "../objects/userTeamList";
import { cloneDeep } from "lodash";
import { Column } from "../objects/column";

const initialState: UserTeamState = {
    user_team_list: [],
    user_team_list_loaded: false,
    user_team_list_refresh: false,
    user_team_list_configuration: EMPTY_USER_TEAM_LIST_CONFIGURATION,
    user_team_list_loading: false,
    user_team_list_count: 0,
    user_team_list_criteria: EMPTY_USER_TEAM_LIST_CRITERIA,
    user_team_is_creating: false,
    user_team: null,
    user_team_delete: null,
    first_preference_loaded: false,
};


export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setPreferenceReducer = (state: UserTeamState, action: PayloadAction<Preferences[]>): UserTeamState => {
    const user_team_preference = setUserTeamPreference(action.payload, state.user_team_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        user_team_list_configuration: { ...state.user_team_list_configuration, preferences: user_team_preference.preferences, columns: user_team_preference.columns },
        user_team_list_criteria: { ...state.user_team_list_criteria, order: user_team_preference.order, order_by: user_team_preference.order_by },
    };
};


const toggleUserTeamRefreshListReducer = (state: UserTeamState): UserTeamState => ({
    ...state, user_team_list_refresh: !state.user_team_list_refresh, user_team_list_loaded: false
});
const addUserTeamListConfigurationPreferenceReducer = (state: UserTeamState, action: PayloadAction<Preferences[]>): UserTeamState => ({
    ...state, user_team_list_configuration: { ...state.user_team_list_configuration, preferences: [...state.user_team_list_configuration.preferences, ...action.payload] }
});
const setUserTeamListConfigurationPreferenceReducer = (state: UserTeamState, action: PayloadAction<Preferences[]>): UserTeamState => ({
    ...state, user_team_list_configuration: { ...state.user_team_list_configuration, preferences: action.payload }
});
const updateUserTeamListReducer = (state: UserTeamState, action: PayloadAction<UserTeam[]>): UserTeamState => ({
    ...state, user_team_list: action.payload, user_team_list_loaded: true
});
const updateUserTeamListCriteriaReducer = (state: UserTeamState, action: PayloadAction<UserTeamListCriteria>): UserTeamState => ({
    ...state, user_team_list_criteria: action.payload
});
const updateUserTeamListLoadingReducer = (state: UserTeamState, action: PayloadAction<boolean>): UserTeamState => ({
    ...state, user_team_list_loading: action.payload
});
const updateUserTeamListCountReducer = (state: UserTeamState, action: PayloadAction<number>): UserTeamState => ({
    ...state, user_team_list_count: action.payload
});
const updateUserTeamIsCreatingReducer = (state: UserTeamState, action: PayloadAction<boolean>): UserTeamState => ({
    ...state, user_team_is_creating: action.payload
});
const toggleUserTeamColumnReducer = (state: UserTeamState, action: PayloadAction<number>): UserTeamState => {
    const columns = cloneDeep([...state.user_team_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, user_team_list_configuration: { ...state.user_team_list_configuration, columns } };
};
const setUserTeamColumnsReducer = (state: UserTeamState, action: PayloadAction<Column[]>): UserTeamState => ({
    ...state, user_team_list_configuration: { ...state.user_team_list_configuration, columns: action.payload }
});
const updateUserTeamReducer = (state: UserTeamState, action: PayloadAction<UserTeam | null>): UserTeamState => ({
    ...state, user_team: action.payload
});
const setUserTeamDeleteReducer = (state: UserTeamState, action: PayloadAction<UserTeam | null>): UserTeamState => ({
    ...state, user_team_delete: action.payload
});


export const userTeam = createSlice({
    name: 'user_team',
    initialState,
    reducers: {
        toggleUserTeamRefreshList: toggleUserTeamRefreshListReducer,
        addUserTeamListConfigurationPreference: addUserTeamListConfigurationPreferenceReducer,
        setUserTeamListConfigurationPreference: setUserTeamListConfigurationPreferenceReducer,
        updateUserTeamList: updateUserTeamListReducer,
        updateUserTeamListCriteria: updateUserTeamListCriteriaReducer,
        updateUserTeamListLoading: updateUserTeamListLoadingReducer,
        updateUserTeamListCount: updateUserTeamListCountReducer,
        updateUserTeamIsCreating: updateUserTeamIsCreatingReducer,
        setUserTeamColumns: setUserTeamColumnsReducer,
        toggleUserTeamColumn: toggleUserTeamColumnReducer,
        updateUserTeam: updateUserTeamReducer,
        setUserTeamDelete: setUserTeamDeleteReducer,
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    toggleUserTeamRefreshList,
    addUserTeamListConfigurationPreference,
    setUserTeamListConfigurationPreference,
    updateUserTeamList,
    updateUserTeamListCriteria,
    updateUserTeamListLoading,
    updateUserTeamListCount,
    updateUserTeamIsCreating,
    setUserTeamColumns,
    toggleUserTeamColumn,
    updateUserTeam,
    setUserTeamDelete,
} = userTeam.actions;